import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  useDeferredValue,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button as MuiButton,
  Chip,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import { toast } from "react-hot-toast";
import { PaginatedTable } from "../../components";
import { baseAxios } from "../../utils/config";
import { colors } from "..";
import EditStylistServiceDialog from "../Services/edit-stylist-service";

const STATUS_CHIP_COLOR = {
  Active: "success",
  Inactive: "default",
};

const MARK_STYLE = {
  backgroundColor: "#fff882",
  color: "inherit",
  borderRadius: 4,
};

function HighlightText({ text, query }) {
  const str = String(text ?? "");
  const q = String(query ?? "").trim();
  if (!q || !str) return str || null;

  const tokens = q
    .split(/\s+/)
    .filter(Boolean)
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (!tokens.length) return str;

  let pattern;
  try {
    pattern = new RegExp(`(${tokens.join("|")})`, "gi");
  } catch {
    return str;
  }

  const parts = str.split(pattern);
  if (parts.length === 1) return str;

  return parts.map((part, i) =>
    pattern.test(part) ? (
      <mark key={i} style={MARK_STYLE}>
        {part}
      </mark>
    ) : (
      part
    )
  );
}

function HeaderColumnFilter({
  ariaLabel,
  value,
  onChange,
  anchorEl,
  setAnchorEl,
  options,
  allMenuLabel,
}) {
  const active = value !== "" && value != null;
  return (
    <Box
      component="span"
      onMouseDown={(e) => e.stopPropagation()}
      sx={{ display: "inline-flex", alignItems: "center", ml: 0.25 }}
    >
      <IconButton
        size="small"
        aria-label={ariaLabel}
        aria-haspopup="true"
        aria-expanded={Boolean(anchorEl)}
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl((prev) =>
            prev === e.currentTarget ? null : e.currentTarget
          );
        }}
        color={active ? "primary" : "default"}
        sx={{
          p: 0.35,
          color: active ? "primary.main" : "rgba(255, 255, 255, 0.85)",
          opacity: active ? 1 : 0.85,
          "&:hover": {
            color: "common.white",
            backgroundColor: "rgba(255, 255, 255, 0.08)",
          },
        }}
      >
        <FilterListIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: { maxWidth: 360, mt: 0.5 },
          },
        }}
      >
        <MenuItem
          selected={value === ""}
          onClick={() => {
            onChange("");
            setAnchorEl(null);
          }}
        >
          {allMenuLabel}
        </MenuItem>
        {options.map((opt) => (
          <MenuItem
            key={String(opt.value)}
            selected={value === opt.value}
            onClick={() => {
              onChange(opt.value);
              setAnchorEl(null);
            }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

function serviceMatchesSearch(row, rawQuery) {
  const q = String(rawQuery ?? "").trim().toLowerCase();
  if (!q) return true;

  const blob = [
    row.service_name,
    row.service_shortcut,
    row.description,
    row.service_status,
  ]
    .map((x) => String(x ?? "").toLowerCase())
    .join(" ");

  const tokens = q.split(/\s+/).filter(Boolean);
  return tokens.every((tok) => blob.includes(tok));
}

function compareServiceRows(a, b, field) {
  const va = a[field];
  const vb = b[field];
  const sa = va == null ? "" : String(va);
  const sb = vb == null ? "" : String(vb);
  const primary = sa.localeCompare(sb, undefined, { sensitivity: "base" });
  if (primary !== 0) return primary;
  return (Number(a.service_id) || 0) - (Number(b.service_id) || 0);
}

function stylistDisplayName(stylist) {
  if (!stylist) return "stylist";
  const first = String(stylist.user_first_name ?? "").trim();
  const last = String(stylist.user_last_name ?? "").trim();
  if (first && last) return `${first} ${last}`;
  return first || last || "stylist";
}

export default function StylistServices() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(search);
  const userId = parseInt(queryParams.get("param"), 10);

  const [stylist, setStylist] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const deferredSearch = useDeferredValue(searchInput);
  const [filterStatus, setFilterStatus] = useState("");
  const [statusFilterMenuAnchor, setStatusFilterMenuAnchor] = useState(null);
  const [sortBy, setSortBy] = useState("service_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editServiceId, setEditServiceId] = useState(null);
  const [editUserId, setEditUserId] = useState(null);

  const stylistName = stylistDisplayName(stylist);

  const loadStylist = useCallback(async () => {
    if (!Number.isFinite(userId)) return;
    try {
      const res = await baseAxios.get(`stylists/${userId}`);
      setStylist(res.data);
    } catch (error) {
      console.error("Error fetching stylist:", error);
      setStylist(null);
      toast.error("Could not load stylist.", { duration: 3000 });
    }
  }, [userId]);

  const loadServices = useCallback(() => {
    if (!Number.isFinite(userId)) return;
    setLoading(true);
    baseAxios
      .get(`/services/getUserServices/${userId}`)
      .then((res) => {
        const data = res?.data;
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];
        setServices(list);
      })
      .catch(() => {
        setServices([]);
        toast.error("Could not load stylist services.", { duration: 3000 });
      })
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    loadStylist();
    loadServices();
  }, [loadStylist, loadServices]);

  const statusFilterOptions = useMemo(() => {
    const set = new Set();
    for (const row of services) {
      if (row.service_status) set.add(row.service_status);
    }
    return [...set].sort((a, b) =>
      String(a).localeCompare(String(b), undefined, { sensitivity: "base" })
    );
  }, [services]);

  const filteredServices = useMemo(() => {
    return services.filter((row) => {
      if (!serviceMatchesSearch(row, deferredSearch)) return false;
      if (filterStatus && row.service_status !== filterStatus) return false;
      return true;
    });
  }, [services, deferredSearch, filterStatus]);

  const sortedServices = useMemo(() => {
    const copy = [...filteredServices];
    copy.sort((a, b) => {
      const c = compareServiceRows(a, b, sortBy);
      return sortOrder === "asc" ? c : -c;
    });
    return copy;
  }, [filteredServices, sortBy, sortOrder]);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const openAddServices = useCallback(() => {
    navigate({
      pathname: "/add-service-to-stylist",
      search: `?param=${userId}`,
    });
  }, [navigate, userId]);

  const openEditService = useCallback(
    (row) => {
      setEditUserId(row.user_id ?? userId);
      setEditServiceId(row.service_id);
      setEditDialogOpen(true);
    },
    [userId]
  );

  const closeEditDialog = useCallback(() => {
    setEditDialogOpen(false);
    setEditServiceId(null);
    setEditUserId(null);
  }, []);

  const onEditSaved = useCallback(() => {
    loadServices();
  }, [loadServices]);

  const columns = useMemo(() => {
    const sq = deferredSearch;
    return [
      {
        id: "service_name",
        label: "Service Name",
        field: "service_name",
        minWidth: 180,
        render: (value) =>
          value ? <HighlightText text={value} query={sq} /> : "-",
      },
      {
        id: "service_shortcut",
        label: "Service Shortcut",
        field: "service_shortcut",
        minWidth: 140,
        render: (value) =>
          value ? <HighlightText text={value} query={sq} /> : "-",
      },
      {
        id: "description",
        label: "Description",
        field: "description",
        minWidth: 220,
        render: (value) =>
          value ? <HighlightText text={value} query={sq} /> : "-",
      },
      {
        id: "service_status",
        label: "Service Status",
        field: "service_status",
        minWidth: 140,
        headerExtras: (
          <HeaderColumnFilter
            ariaLabel="Filter by status"
            value={filterStatus}
            onChange={setFilterStatus}
            anchorEl={statusFilterMenuAnchor}
            setAnchorEl={setStatusFilterMenuAnchor}
            allMenuLabel="All statuses"
            options={statusFilterOptions.map((s) => ({
              value: s,
              label: s,
            }))}
          />
        ),
        render: (value) => {
          if (!value) return "-";
          return (
            <Chip
              size="small"
              variant="filled"
              label={value}
              color={STATUS_CHIP_COLOR[value] ?? "default"}
              sx={{ fontWeight: 500, lineHeight: 1 }}
            />
          );
        },
      },
      {
        id: "actions",
        label: "Actions",
        sortable: false,
        minWidth: 120,
        render: (_, row) => (
          <MuiButton
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<EditIcon sx={{ fontSize: 14 }} />}
            onClick={() => openEditService(row)}
            sx={{
              minHeight: 24,
              py: 0.25,
              px: 1,
              textTransform: "none",
              fontSize: "0.75rem",
              color: (theme) =>
                theme.palette.mode === "dark"
                  ? theme.palette.text.primary
                  : colors.black,
              "& .MuiButton-startIcon": { mr: 0.5, color: "primary.main" },
            }}
          >
            Edit
          </MuiButton>
        ),
      },
    ];
  }, [
    deferredSearch,
    filterStatus,
    statusFilterMenuAnchor,
    statusFilterOptions,
    openEditService,
  ]);

  return (
    <Box sx={{ px: 8, py: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mt: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
            Manage Services: {stylistName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and edit services assigned to this stylist.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search name, shortcut, or description…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            variant="outlined"
            sx={{
              minWidth: 280,
              maxWidth: "100%",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
                borderRadius: "6px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0.75 }}>
                  <SearchIcon sx={{ fontSize: 20, color: "grey.400" }} />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end" sx={{ mr: 0.75 }}>
                  <IconButton
                    size="small"
                    onClick={() => setSearchInput("")}
                    edge="end"
                    aria-label="Clear search"
                    sx={{
                      color: "grey.500",
                      "&:hover": {
                        color: "grey.800",
                        backgroundColor: "grey.200",
                      },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <IconButton
            aria-label="Refresh stylist services"
            onClick={loadServices}
            disabled={loading}
            size="medium"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              borderRadius: "6px",
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            <RefreshIcon />
          </IconButton>
          <MuiButton
            variant="contained"
            color="primary"
            onClick={openAddServices}
            sx={{
              minHeight: 40,
              px: 2,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            <AddIcon sx={{ mr: 0.75 }} /> Add Services
          </MuiButton>
        </Box>
      </Box>

      <PaginatedTable
        columns={columns}
        rows={sortedServices}
        totalRowCount={sortedServices.length}
        loading={loading}
        onPaginationChange={noopPagination}
        onSortChange={handleSortChange}
        initialOrderBy="service_name"
        initialOrder="asc"
        searchQuery={`${deferredSearch}|${filterStatus}`}
        clientSidePagination
        skeletonHeight="40px"
      />

      <EditStylistServiceDialog
        open={editDialogOpen}
        userId={editUserId}
        serviceId={editServiceId}
        stylistName={stylistName}
        onClose={closeEditDialog}
        onSaved={onEditSaved}
      />
    </Box>
  );
}
